/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of a blank screen.
 */
import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      reported: false 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Send error to backend for logging
    this.reportError(error, errorInfo);
  }

  // Check if error is caused by browser extensions (Google Translate, etc.)
  isExtensionError(error) {
    const extensionErrors = [
      'removeChild',
      'insertBefore',
      'The node to be removed is not a child of this node',
      'Cannot read properties of null (reading \'removeChild\')',
      'Failed to execute \'removeChild\'',
      'Failed to execute \'insertBefore\'',
      'NotFoundError: Node was not found',
      'script error',
      'ResizeObserver loop',
    ];
    
    const errorMessage = error?.message || '';
    const errorStack = error?.stack || '';
    
    return extensionErrors.some(ext => 
      errorMessage.toLowerCase().includes(ext.toLowerCase()) ||
      errorStack.toLowerCase().includes(ext.toLowerCase())
    );
  }

  async reportError(error, errorInfo) {
    // Skip reporting errors caused by browser extensions
    if (this.isExtensionError(error)) {
      console.log('Skipping extension-related error:', error?.message);
      return;
    }

    try {
      const errorId = 'ERR_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
      
      const errorData = {
        error_id: errorId,
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        component_stack: errorInfo?.componentStack || '',
        url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        user_email: this.getUserEmail(),
        session_id: sessionStorage.getItem('analytics_session_id'),
        local_storage_keys: Object.keys(localStorage),
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
      };

      // Send to backend
      await fetch(`${API_URL}/api/errors/frontend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
      });

      this.setState({ errorId, reported: true });
    } catch (e) {
      console.error('Failed to report error:', e);
    }
  }

  getUserEmail() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || null;
      }
    } catch (e) {}
    return null;
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearAndRefresh = () => {
    // Clear potentially corrupted data
    localStorage.removeItem('token');
    sessionStorage.clear();
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId, reported } = this.state;
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            
            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              ¡Ups! Algo salió mal
            </h1>
            
            <p className="text-gray-600 mb-6">
              Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado automáticamente.
            </p>

            {/* Error ID for support */}
            {errorId && (
              <div className="bg-gray-100 rounded-lg p-3 mb-6">
                <p className="text-xs text-gray-500 mb-1">Código de error (para soporte):</p>
                <code className="text-sm font-mono text-gray-700 select-all">{errorId}</code>
              </div>
            )}

            {/* Status */}
            {reported && (
              <p className="text-sm text-green-600 mb-6 flex items-center justify-center gap-2">
                <Bug className="w-4 h-4" />
                Error reportado automáticamente
              </p>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={this.handleRefresh}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Recargar página
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
              >
                <Home className="w-5 h-5" />
                Ir al inicio
              </button>

              <button
                onClick={this.handleClearAndRefresh}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2 underline"
              >
                Limpiar sesión y reintentar
              </button>
            </div>

            {/* Error details (collapsible, for debugging) */}
            <details className="mt-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                Detalles técnicos
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs font-mono text-red-600 overflow-auto max-h-32">
                {error?.message || 'Error desconocido'}
              </div>
            </details>

            {/* Contact support */}
            <p className="mt-6 text-xs text-gray-400">
              Si el problema persiste, contacta a soporte con el código de error.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
